import { ref } from "vue";
import { defaultApi, type IResponse } from "@/api";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import type { WidgetType } from "@/features/control-tower/components/widget-catalogue-card";
import { Widget, WidgetCatalogItem } from "@/features/control-tower/interfaces/widget-catalogue";
import { WIDGET_TYPES } from "@/features/control-tower/constants/common";

export interface WidgetCatalogResponse {
  widgets: WidgetCatalogItem[];
}

export const useWidgetCatalog = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const widgets = ref<Widget[]>([]);

  /**
   * Maps widget appearance type to WidgetType
   */
  const mapToWidgetType = (type: string): WidgetType => {
    const typeMap: Record<string, WidgetType> = {
      [WIDGET_TYPES.METRIC]: WIDGET_TYPES.METRIC,
      [WIDGET_TYPES.CHART_BAR]: WIDGET_TYPES.CHART_BAR,
      [WIDGET_TYPES.CHART_PIE]: WIDGET_TYPES.CHART_PIE,
      [WIDGET_TYPES.MAP]: WIDGET_TYPES.MAP,
      [WIDGET_TYPES.LIST_SIMPLE]: WIDGET_TYPES.LIST_SIMPLE,
      [WIDGET_TYPES.LIST_TABLE]: WIDGET_TYPES.LIST_TABLE
    };
    return typeMap[type] || WIDGET_TYPES.METRIC;
  };

  /**
   * Transforms API response to Widget format
   */
  const transformWidgetCatalog = (catalogItems: WidgetCatalogItem[]): Widget[] => {
    return catalogItems.map((item) => ({
      id: item.catalog_id,
      icon: item.icon,
      title: item.title,
      widgetType: mapToWidgetType(item.appearance.type),
      description: item.description,
      default_config: item.default_config,
      badge: item.labels?.[0]?.title,
      product: item.product,
      selected: false
    }));
  };

  const fetchWidgetCatalog = async (): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Fetching widget catalog from API");
    try {
      loading.value = true;
      error.value = null;
      const response = await defaultApi.apiVersion1.widgetCatalog.getAll<IResponse>();

      if (response.data) {
        const catalogData = response.data as WidgetCatalogResponse;
        widgets.value = transformWidgetCatalog(catalogData.widgets || []);
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget catalog fetched successfully. Count: ${widgets.value.length}`);
      } else {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Widget catalog API call completed but no data received");
      }

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widget catalog";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "widgetCatalog/getAll",
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget catalog: ${errorMessage}`);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "widgetCatalog/getAll"
      });
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    widgets,
    fetchWidgetCatalog
  };
};
